import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";
import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";
import AccordionDownArrowIcon from "../../common/icons/AccordionDownArrowIcon";
import AccordionUpArrowIcon from "../../common/icons/AccordionUpArrowIcon";
import { SortIconAscending, SortIconDescending } from "../../packages/SVGs";
import "./index.scss";

import {
  ELEMENT_COLOR_PALETTE,
  ELEMENT_BG_PALETTE,
  GREY_BG,
  GREY_BORDER,
} from "../../constants/global.constants.js";

function getColorIndexes(elements) {
  const valueFirstIndex = {};
  elements.forEach((val, idx) => {
    if (val && valueFirstIndex[val] === undefined) {
      valueFirstIndex[val] = idx;
    }
  });
  return elements.map((val) => {
    if (!val) return -1;
    const firstIdx = valueFirstIndex[val];
    return firstIdx % ELEMENT_COLOR_PALETTE.length;
  });
}

const ElementSelectionDialog = forwardRef(function ElementSelectionDialog(
  { numElements = 8, elementOptions = [], initialValues = [], isISD = false },
  ref,
) {
  const { t } = useTranslation();
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [elements, setElements] = useState(() => {
    const initial =
      initialValues.length == numElements &&
      Array.isArray(initialValues) &&
      initialValues.every((v) => v !== undefined && v !== null)
        ? [...initialValues]
        : Array(numElements).fill("");
    return initial;
  });

  useEffect(() => {
    setElements((prev) => {
      if (
        initialValues.length == numElements &&
        Array.isArray(initialValues) &&
        initialValues.length > 0
      ) {
        return [...initialValues];
      }
      if (numElements > prev.length) {
        return [...prev, ...Array(numElements - prev.length).fill("")];
      }
      return prev.slice(0, numElements);
    });
  }, [numElements, initialValues]);

  const handleChange = (idx, value) => {
    setElements((prevElements) => {
      const newElements = [...prevElements];
      for (let i = idx; i < newElements.length; i++) {
        newElements[i] = value;
      }
      return newElements;
    });
  };

  const colorIndexes = getColorIndexes(elements);

  const sortedOptions = useMemo(() => {
    return [...elementOptions].sort((a, b) =>
      sortOrder === "asc" ? a.localeCompare(b) : b.localeCompare(a),
    );
  }, [elementOptions, sortOrder]);

  const selectOptions = useMemo(() => {
    const options = sortedOptions.map((opt) => ({
      value: opt,
      label: opt,
    }));
    return [{ label: t("addNewPlant.elements"), options }];
  }, [sortedOptions, t]);

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const DropdownIndicator = (props) =>
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <AccordionUpArrowIcon />
        ) : (
          <AccordionDownArrowIcon />
        )}
      </components.DropdownIndicator>
    );

  const SingleValue = ({ children, ...props }) => (
    <components.SingleValue {...props}>{children}</components.SingleValue>
  );

  const formatGroupLabel = (data) => (
    <div
      className="element-dropdown-header"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className="element-dropdown-header-label">{data.label}</span>
      <button
        type="button"
        className="element-dropdown-sort-button"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSortToggle();
        }}
        title={
          sortOrder === "asc"
            ? t("addNewPlant.sortDescending")
            : t("addNewPlant.sortAscending")
        }
      >
        {sortOrder === "asc" ? <SortIconAscending /> : <SortIconDescending />}
      </button>
    </div>
  );

  useImperativeHandle(ref, () => {
    const allSelected = elements.every((el) => el && el !== "");
    const ro_nf_element_used = isISD ? elements : elements[0] || "";
    return { allSelected, elements, ro_nf_element_used };
  }, [elements, isISD]);

  return (
    <div>
      <div className="element-selection-container">
        {elements.map((val, idx) => {
          const colorIdx = colorIndexes[idx];
          const bgColor =
            colorIdx === -1 ? GREY_BG : ELEMENT_BG_PALETTE[colorIdx];
          const borderColor =
            colorIdx === -1 ? GREY_BORDER : ELEMENT_COLOR_PALETTE[colorIdx];
          return (
            <div
              key={idx}
              className="element-card"
              style={{
                background: bgColor,
                borderTop: `4px solid ${borderColor}`,
                borderRight: `1px solid ${borderColor}`,
                borderLeft: `1px solid ${borderColor}`,
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <div className="element-card-label">
                {t("addNewPlant.elementLabel", { number: idx + 1 })}
              </div>
              <CustomTooltip
                text={hoveredIndex === idx ? (val || t("addNewPlant.selectElement")) : ""}
                placement="bottom"
              >
                <div
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Select
                    isSearchable
                    isClearable={false}
                    options={selectOptions}
                    classNamePrefix="select"
                    placeholder={t("addNewPlant.selectElement")}
                    formatGroupLabel={formatGroupLabel}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    value={
                      val
                        ? selectOptions[0].options.find(
                            (opt) => opt.value === val,
                          )
                        : null
                    }
                    onChange={(selectedOption) => {
                      const newValue = selectedOption
                        ? selectedOption.value
                        : "";
                      handleChange(idx, newValue);
                    }}
                    closeMenuOnSelect={true}
                    noOptionsMessage={() => t("addNewPlant.noElementsFound")}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 32,
                        height: 32,
                        border:
                          val === "" ? "1px solid #e53935" : "1px solid #ccc",
                        borderRadius: 4,
                        fontSize: 14,
                        boxShadow: state.isFocused ? "0 0 0 1px #ccc" : "none",
                        "&:hover": {
                          borderColor: val === "" ? "#e53935" : "#999",
                        },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: 32,
                        padding: "0 6px",
                      }),
                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: 32,
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#bdbdbd",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menu: (base) => ({
                        ...base,
                        overflow: "hidden",
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: 250,
                        overflowY: "auto",
                        paddingTop: 0,
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                      }),
                      group: (base) => ({
                        ...base,
                        paddingTop: 0,
                        paddingBottom: 0,
                        margin: 0,
                      }),
                      groupHeading: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                        marginBottom: 0,
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 14,
                        backgroundColor: state.isSelected
                          ? "#e0e0e0"
                          : state.isFocused
                            ? "#f5f5f5"
                            : "#fff",
                        color: "#000",
                        cursor: "pointer",
                        padding: "8px 12px",
                        "&:active": {
                          backgroundColor: "#e0e0e0",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: 14,
                      }),
                    }}
                    components={{
                  DropdownIndicator,
                  SingleValue,
                }}
              />
                </div>
              </CustomTooltip>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ElementSelectionDialog;
